import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Copy, Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGES = [['/', 'Home'], ['/shop', 'Shop'], ['/shop#founders-collections', "Founder's Collections"], ['/freebie', 'Free checklist'], ['/blog', 'Blog'],
  ['/destinations/italy', 'Italy guides'], ['/destinations/portugal', 'Portugal guides'], ['/destinations/spain', 'Spain guides'], ['/destinations/canada', 'Canada guides'],
  ['/destinations/bali', 'Bali guides'], ['/destinations/thailand', 'Thailand guides']];
const inputCls = 'w-full p-2.5 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm bg-white';
const aud = (n) => `A$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}`;

const copy = (url) => navigator.clipboard?.writeText(url).then(() => toast.success('Link copied')).catch(() => toast.info(url));

const Builder = ({ channels, authHeaders, onCreated }) => {
  const [f, setF] = useState({ name: '', channel: 'google_ads', page: '/', custom: '' });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const path = f.page === 'custom' ? f.custom : f.page;
      const r = await axios.post(`${API}/admin/campaign-links`, { name: f.name, channel: f.channel, path }, authHeaders);
      copy(r.data.link.url); setF({ ...f, name: '' }); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create link'); }
    setBusy(false);
  };
  return (
    <div className="grid md:grid-cols-4 gap-3 items-end" data-testid="campaign-builder">
      <div><label className="block text-xs text-[#1E1622]/60 mb-1">Page</label>
        <select value={f.page} onChange={(e) => setF({ ...f, page: e.target.value })} data-testid="campaign-page" className={inputCls}>
          {PAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}<option value="custom">Other page…</option>
        </select>
        {f.page === 'custom' && <input value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })} placeholder="/shop/guides/gubbio" data-testid="campaign-custom-path" className={`${inputCls} mt-2`} />}
      </div>
      <div><label className="block text-xs text-[#1E1622]/60 mb-1">Channel</label>
        <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="campaign-channel" className={inputCls}>
          {channels.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div><label className="block text-xs text-[#1E1622]/60 mb-1">Campaign name</label>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Spring Italy Ad" data-testid="campaign-name" className={inputCls} />
      </div>
      <button onClick={create} disabled={busy || !f.name.trim()} data-testid="campaign-create" className="btn-primary flex items-center justify-center gap-2 text-sm disabled:opacity-50"><Plus size={16} /> Create &amp; copy link</button>
    </div>
  );
};

const LinkRow = ({ l, onDelete }) => (
  <tr className="border-t border-[#F5EFE9] align-top" data-testid={`campaign-row-${l.campaign}`}>
    <td className="py-3 pr-3"><p className="font-medium text-[#1E1622]">{l.name}</p><p className="text-xs text-[#1E1622]/45 break-all">{l.url}</p></td>
    <td className="py-3 pr-3 text-[#1E1622]/70">{l.channel_label}</td>
    <td className="py-3 text-right" data-testid={`campaign-visits-${l.campaign}`}>{l.stats.visits}</td>
    <td className="py-3 text-right" data-testid={`campaign-orders-${l.campaign}`}>{l.stats.orders}</td>
    <td className="py-3 text-right text-[#3B164C] font-medium" data-testid={`campaign-revenue-${l.campaign}`}>{aud(l.stats.revenue)}</td>
    <td className="py-3 pl-3 whitespace-nowrap text-right">
      <button onClick={() => copy(l.url)} title="Copy link" data-testid={`campaign-copy-${l.campaign}`} className="p-1.5 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg"><Copy size={15} /></button>
      <button onClick={() => onDelete(l)} title="Delete" data-testid={`campaign-delete-${l.campaign}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={15} /></button>
    </td>
  </tr>
);

export const CampaignLinksPanel = ({ authHeaders }) => {
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/admin/campaign-links`, authHeaders).then((r) => setD(r.data)).catch(() => toast.error('Could not load campaign links'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (l) => { if (!window.confirm(`Delete the "${l.name}" link? Its results stay in your analytics.`)) return; await axios.delete(`${API}/admin/campaign-links/${l.id}`, authHeaders); load(); };
  if (!d) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6 space-y-5" data-testid="campaign-links-panel">
      <div>
        <h3 className="font-semibold text-[#1E1622] flex items-center gap-2"><Link2 size={18} className="text-[#C5A059]" /> Campaign Links</h3>
        <p className="text-sm text-[#1E1622]/60">Create a tracked link for each ad, email or social post, then see which ones bring visitors and buyers (last 30 days).</p>
      </div>
      <Builder channels={d.channels} authHeaders={authHeaders} onCreated={load} />
      {d.links.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-[#1E1622]/50"><th className="pb-2 font-medium">Campaign</th><th className="pb-2 font-medium">Channel</th><th className="pb-2 font-medium text-right">Visits</th><th className="pb-2 font-medium text-right">Orders</th><th className="pb-2 font-medium text-right">Revenue</th><th /></tr></thead>
            <tbody>{d.links.map((l) => <LinkRow key={l.id} l={l} onDelete={remove} />)}</tbody>
          </table>
        </div>
      ) : <p className="text-sm text-[#1E1622]/50" data-testid="campaign-links-empty">No campaign links yet.</p>}
      {d.other_campaigns.length > 0 && (
        <p className="text-xs text-[#1E1622]/50" data-testid="campaign-other">Other tagged visits: {d.other_campaigns.map((c) => `${c.campaign} (${c.visits} visits, ${aud(c.revenue)})`).join(' · ')}</p>
      )}
    </div>
  );
};
